/* import __COLOCATED_TEMPLATE__ from './chips-line-clamp.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import clsx from 'clsx';

/**
 * @typedef {object} Themes
 * @property {string} [BLACK] - For light palette
 * @property {string} [WHITE] - For dark palette
 * @property {string} [NONE] - No color declarations
 */

/**
 * @param {HTMLElement} element
 * @returns {CanvasRenderingContext2D}
 */
const createContext = (element) => {
  const {
    fontFamily,
    fontSize,
    letterSpacing,
  } = window.getComputedStyle(element);

  const context = document
    .createElement('canvas')
    .getContext('2d');

  context.font = `${fontSize} ${fontFamily}`;
  context.letterSpacing = letterSpacing;
  context.textRendering = 'optimizeSpeed';

  return context;
};

const CLASS_NAME = 'GLM-chips-line-clamp';
const DEFAULT_CHIP_LABEL_KEY = 'name';
const DEFAULT_SELECTED_CHIP_ID = 'id';
const DEFAULT_GAP_PX = 4;
const DEFAULT_SIDE_PADDING_PX = 26;
const DEFAULT_VERIFIED_SPACE_PX = 24;
const RESERVED_HAS_MORE_SPACE_PX = 75;

/** @type {Themes} */
const THEMES = {
  BLACK: 'black',
  WHITE: 'white',
  NONE: 'none',
};

const THEME_TO_CLASS_MAP = Object.keys(THEMES).reduce((accum, themeKey) => {
  const theme = THEMES[themeKey];
  accum[theme] = `${CLASS_NAME}--theme-${theme}`;
  return accum;
}, {});

class GlmChipsLineClamp extends Component {
  /** @var {Number|null} */
  @tracked containerWidth = null;

  /** @var {CanvasRenderingContext2D|null} */
  @tracked context = null;

  /** @var {HTMLElement|null} */
  @tracked element = null;

  /** @var {string} chipLabelKey accessor for chip model's label */
  chipLabelKey = this.args.chipLabelKey ?? DEFAULT_CHIP_LABEL_KEY;

  /** @var {string} selectedChipsIdentifier accessor for selected chip model's id */
  selectedChipsIdentifier = this.args.selectedChipsIdentifier ?? DEFAULT_SELECTED_CHIP_ID;

  /**
   * Compute the width used by chip text
   */
  #textWidthsMapCache = createCache(() => {
    if (!this.context) {
      return {};
    }

    const {
      chipLabelKey,
      verifiedIconReservedSpace,
    } = this;

    return this.args.chips.reduce((accum, chip) => {
      const chipTextWidth = this.context.measureText(chip[chipLabelKey]).width ?? 0;
      const verifiedIconReservedSpacePx = chip.verified
        ? verifiedIconReservedSpace
        : 0;

      accum[chip.id] = chipTextWidth
        ? chipTextWidth + DEFAULT_SIDE_PADDING_PX + verifiedIconReservedSpacePx
        : 0;
      return accum;
    }, {});
  });

  /**
   * Map the chip's model id to the chip
   */
  #chipIdToChipCache = createCache(() => {
    return this.args.chips.reduce((accum, chip) => {
      accum[chip.id] = chip;
      return accum;
    }, {});
  });

  /**
   * Map the selected chip's specified identifier to the chip
   */
  #selectedChipIdToChipCache = createCache(() => {
    const { selectedChipsIdentifier } = this;
    return (this.args.selectedChips ?? []).reduce((accum, chip) => {
      accum[chip[selectedChipsIdentifier]] = chip;
      return accum;
    }, {});
  });

  /**
   * Calculate how many chips can be shown on the available line space
   */
  #visibleChipsCache = createCache(() => {
    const {
      containerWidth,
      lineClamp,
      sortedChips,
      textWidthsMap,
    } = this;

    if (!containerWidth) {
      return [];
    }

    // if only 1 line, immediately take away reserved space from available
    const usableWidth = (lineClamp === 1 && this.args.showRemainingCounter)
      ? containerWidth - RESERVED_HAS_MORE_SPACE_PX - DEFAULT_GAP_PX
      : containerWidth;

    // create an object to store our remaining space per line
    const lineToAvailableSpaceTuples = Array.from({ length: lineClamp }, (_, index) => [index, usableWidth]);
    const availableSpacePerLineMap = Object.fromEntries(lineToAvailableSpaceTuples);

    let currentLine = 0;
    let chipIndex = 0;
    let visibleChips = [];

    while (availableSpacePerLineMap[currentLine]) {
      const chip = sortedChips[chipIndex];

      // no more lines or no more chips to show
      if (!availableSpacePerLineMap[currentLine] || !chip) {
        break;
      }

      const chipWidth = textWidthsMap[chip.id];

      // show chip if there is room for it
      if (availableSpacePerLineMap[currentLine] - chipWidth >= 0) {
        // show chip if there is room for it
        availableSpacePerLineMap[currentLine] = availableSpacePerLineMap[currentLine] - chipWidth - DEFAULT_GAP_PX;
        visibleChips.push(chip);
        chipIndex++;
      } else if (
        chipIndex === sortedChips.length - 1
        && currentLine === lineClamp - 1
        && availableSpacePerLineMap[currentLine] + RESERVED_HAS_MORE_SPACE_PX + DEFAULT_GAP_PX - chipWidth >= 0
      ) {
        // if this is the last item and there would be room if reserved space given back
        visibleChips.push(chip);
        chipIndex++;
        break;
      } else {
        currentLine++;

        // last line needs to account for reserved space
        if (currentLine === lineClamp - 1) {
          availableSpacePerLineMap[currentLine] = availableSpacePerLineMap[currentLine] - RESERVED_HAS_MORE_SPACE_PX - DEFAULT_GAP_PX;
        }
      }
    }

    return visibleChips;
  });

  /**
   * Sort chips so that any selected chips are shown first
   */
  #sortedChipsCache = createCache(() => {
    const { selectedChipIds } = this;
    const { chips } = this.args;

    // return selected items first
    return chips.toArray().sort((a, b) => {
      const isInB = selectedChipIds.includes(b.id) - selectedChipIds.includes(a.id);
      return isInB || chips.indexOf(a) - chips.indexOf(b);
    });
  });

  /**
   * @returns {string}
   */
  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
      THEME_TO_CLASS_MAP[this.args.theme || THEMES.BLACK],
    );
  }

  /**
   * @returns {string}
   */
  get className() {
    return CLASS_NAME;
  }

  /**
   * @returns {SafeString}
   */
  get chipStyles() {
    const sidePadding = this.args.sidePadding ?? DEFAULT_SIDE_PADDING_PX;

    return htmlSafe(`
      --chip-padding-ew: ${sidePadding}px;
    `);
  }

  /**
   * @returns {SafeString}
   */
  get chipContainerStyles() {
    const { gap } = this;
    const shouldWrap = this.lineClamp === 1
      ? 'nowrap'
      : 'wrap';

    return htmlSafe(`
      --chip-gap: ${gap}px;
      --chip-flex-wrap: ${shouldWrap};
    `);
  }

  /**
   * @returns {Object}
   */
  get chipIdToChipCache() {
    return getValue(this.#chipIdToChipCache);
  }

  get verifiedIconReservedSpace() {
    const { size } = this.args;

    return size === 'small'
      ? DEFAULT_VERIFIED_SPACE_PX - 4
      : DEFAULT_VERIFIED_SPACE_PX;
  }

  /**
   * @returns {string|null}
   */
  get hasMoreText() {
    const chipsCount = this.args.chips.length;
    const visibleChipsCount = this.visibleChips.length;
    const hiddenCount = chipsCount - visibleChipsCount;

    if (visibleChipsCount === 0) {
      return `${hiddenCount} total`;
    }

    return hiddenCount
      ? `+${hiddenCount} more`
      : null;
  }

  /**
   * @returns {number}
   */
  get gap() {
    return Number.parseInt(this.args.gap || DEFAULT_GAP_PX);
  }

  /**
   * @returns {number}
   */
  get lineClamp() {
    return Number.parseInt(this.args.lineClamp || 1);
  }

  /**
   * @returns {Array}
   */
  get selectedChipIds() {
    const { selectedChipsIdentifier } = this;
    const { selectedChips } = this.args;

    return (selectedChips ?? []).map(({ [selectedChipsIdentifier]: id }) => id);
  }

  /**
   * @returns {Object}
   */
  get selectedChipIdToChip() {
    return getValue(this.#selectedChipIdToChipCache);
  }

  /**
   * @returns {Array}
   */
  get sortedChips() {
    return getValue(this.#sortedChipsCache);
  }

  /**
   * @returns {Object}
   */
  get textWidthsMap() {
    return getValue(this.#textWidthsMapCache);
  }

  /**
   * @returns {Array}
   */
  get visibleChips() {
    return getValue(this.#visibleChipsCache);
  }

  /**
   * Adjust container's available width on resize
   */
  @action
  handleResize() {
    this.containerWidth = this.element.getBoundingClientRect().width;
  }

  /**
   * @param {HTMLElement|null} element
   */
  @action
  handleInit(element) {
    if (element) {
      this.containerWidth = element.getBoundingClientRect().width;
      this.context = createContext(element);
      this.element = element;
    }
  }
}

export default GlmChipsLineClamp;
